import React, { useEffect, useState } from "react";
import NavBar from "../components/Navbar/NavBar";
import Footer from "../components/Footer";
import { useDocTitle } from "../components/CustomHook";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import Notiflix from "notiflix";
import transparency from "../data/transparency";
import campaigns from "../data/campaigns";

const API_BASE = process.env.REACT_APP_MEMBERSHIP_API || "http://localhost:5001";

// share of each category within total funds used (from public transparency data)
const usedTotal = transparency.totals.used;
const breakdownPct = (category) =>
  (transparency.breakdown.find((b) => b.category === category)?.amount / usedTotal) * 100;

const fmt = (cents) =>
  (cents / 100).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });

// Helpdesk status display config
const statusBadge = {
  open: { label: "Open", cls: "bg-blue-100 text-blue-800" },
  in_progress: { label: "In Progress", cls: "bg-purple-100 text-purple-800" },
  waiting_member: { label: "Waiting on You", cls: "bg-amber-100 text-amber-800" },
  resolved: { label: "Resolved", cls: "bg-green-100 text-green-800" },
  closed: { label: "Closed", cls: "bg-gray-200 text-gray-600" },
};
const categoryLabel = {
  general: "General",
  remote: "🖥️ Remote Session",
  hardware: "🎁 Hardware Assistance",
  digital: "📈 Digital Growth",
  lamna: "🦈 LAMNA Escalation",
};

const Members = () => {
  useDocTitle("ZelT | Member Area");
  const navigate = useNavigate();
  const [member, setMember] = useState(null);
  const [impact, setImpact] = useState(null);
  const [tickets, setTickets] = useState(null);
  const [ticketDetail, setTicketDetail] = useState(null);
  const [health, setHealth] = useState(null);
  const [newTicket, setNewTicket] = useState({ category: "general", subject: "", body: "" });
  const [replyText, setReplyText] = useState("");
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    const token = localStorage.getItem("zelt_token");
    if (!token) {
      navigate("/membership");
      return;
    }

    const headers = { Authorization: `Bearer ${token}` };
    axios
      .get(`${API_BASE}/members-only`, { headers })
      .then((response) => {
        // Employees see ONLY the Staff Workspace — clean separation (no mixing)
        axios
          .get(`${API_BASE}/my-role`, { headers })
          .then((r) => {
            if (r.data.role !== "member") {
              navigate("/staff");
              return;
            }
            setMember(response.data.member);
            setStatus("ready");
          })
          .catch(() => {
            setMember(response.data.member);
            setStatus("ready");
          });
      })
      .catch(() => {
        localStorage.removeItem("zelt_token");
        localStorage.removeItem("zelt_member");
        navigate("/membership");
      });

    // donations: optional — failure just hides the My Impact section
    axios
      .get(`${API_BASE}/my-impact`, { headers })
      .then((response) => setImpact(response.data))
      .catch(() => setImpact(null));

    // tickets: optional — failure just hides My Requests
    axios
      .get(`${API_BASE}/my-tickets`, { headers })
      .then((response) => setTickets(response.data.tickets))
      .catch(() => setTickets(null));

    // ZelGuardian health: optional — failure just hides the Protection Score
    axios
      .get(`${API_BASE}/my-health`, { headers })
      .then((response) => setHealth(response.data))
      .catch(() => setHealth(null));
  }, [navigate]);

  const refreshTickets = (headers) => {
    axios
      .get(`${API_BASE}/my-tickets`, { headers })
      .then((response) => setTickets(response.data.tickets))
      .catch(() => {});
  };

  const createTicket = (e) => {
    e.preventDefault();
    const token = localStorage.getItem("zelt_token");
    const headers = { Authorization: `Bearer ${token}` };
    axios
      .post(`${API_BASE}/tickets`, newTicket, { headers })
      .then((response) => {
        setNewTicket({ category: "general", subject: "", body: "" });
        setTicketDetail(response.data.ticket);
        refreshTickets(headers);
        Notiflix.Report.success(
          "Ticket created!",
          `Ticket #${response.data.ticket.id} — a confirmation email is on its way.`,
          "Okay"
        );
      })
      .catch((error) => {
        Notiflix.Report.failure(
          "Couldn't create ticket",
          error.response?.data?.message || "Please try again.",
          "Okay"
        );
      });
  };

  const openTicket = (id) => {
    const token = localStorage.getItem("zelt_token");
    axios
      .get(`${API_BASE}/tickets/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        setTicketDetail(response.data);
        setReplyText("");
      })
      .catch(() => {});
  };

  const replyTicket = (e) => {
    e.preventDefault();
    if (!ticketDetail || !replyText.trim()) return;
    const token = localStorage.getItem("zelt_token");
    axios
      .post(
        `${API_BASE}/tickets/${ticketDetail.ticket.id}/reply`,
        { body: replyText.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      .then(() => openTicket(ticketDetail.ticket.id))
      .catch(() => {});
  };

  const handleLogout = () => {
    localStorage.removeItem("zelt_token");
    localStorage.removeItem("zelt_member");
    navigate("/");
  };

  if (status === "loading") {
    return (
      <div>
        <NavBar />
        <div className="flex justify-center items-center h-screen">
          <p className="text-xl font-semibold text-gray-500">Verifying your membership...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <NavBar />
      <div id="members" className="mt-8 w-full bg-white py-12 lg:py-24">
        <div className="mx-4 lg:mx-20" data-aos="zoom-in">
          <div className="max-w-3xl mx-auto bg-black p-8 rounded-2xl shadow-2xl">
            <div className="bg-white rounded-xl p-8 text-center">
              <div className="flex items-center justify-center w-20 h-20 mx-auto rounded-full bg-blue-900 text-white text-3xl font-extrabold mb-4 shadow-lg">
                {member?.username?.charAt(0).toUpperCase()}
              </div>
              <h1 className="mb-2 text-3xl md:text-4xl font-bold text-blue-900">
                Welcome back, {member?.username}!
              </h1>
              <p className="text-lg text-gray-500 mb-8">
                You are viewing the members-only area. Thank you for being part
                of the ZelTechnology community.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-left mb-8">
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="font-bold text-blue-900">Username</h3>
                  <p className="text-gray-500">{member?.username}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="font-bold text-blue-900">Email</h3>
                  <p className="text-gray-500">{member?.email}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="font-bold text-blue-900">Member Since</h3>
                  <p className="text-gray-500">
                    {member?.created_at ? new Date(member.created_at).toLocaleDateString() : "—"}
                  </p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <h3 className="font-bold text-blue-900">Membership</h3>
                  <p className="text-gray-500">Free — Active</p>
                </div>
              </div>

              {/* ZELGUARDIAN — Protection Score card */}
              {health && (
                <div className="mb-8 text-left">
                  <div
                    className="rounded-2xl p-6 text-white shadow-lg"
                    style={{ background: "linear-gradient(135deg, #0f172a, #1e3a8a)" }}
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h2 className="text-2xl font-extrabold">🛡️ ZelGuardian</h2>
                        <p className="text-teal-300 text-xs font-semibold">
                          Your Protection Score — updated live
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-4xl font-extrabold text-teal-300">
                          {health.score}
                        </p>
                        <p className="text-gray-400 text-xs">/ 100</p>
                      </div>
                    </div>
                    <div className="w-full h-3 bg-white/10 rounded-full overflow-hidden mb-4">
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{
                          width: `${health.score}%`,
                          background: "linear-gradient(90deg, #12bfbe, #9198e5)",
                        }}
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-3 text-center text-xs">
                      <div className="bg-white/10 rounded-xl p-3">
                        <p className="text-lg font-bold text-teal-300">
                          {health.lessonsCompleted}/3
                        </p>
                        <p className="text-gray-300">Lessons done</p>
                      </div>
                      <div className="bg-white/10 rounded-xl p-3">
                        <p className="text-lg font-bold text-teal-300">
                          {health.devicesRegistered}
                        </p>
                        <p className="text-gray-300">Devices locked</p>
                      </div>
                      <div className="bg-white/10 rounded-xl p-3">
                        <p className="text-lg font-bold text-teal-300">
                          {health.checkupItems === null ? "—" : `${health.checkupItems}/5`}
                        </p>
                        <p className="text-gray-300">Monthly checkup</p>
                      </div>
                    </div>
                    {health.checkupItems === null && (
                      <p className="text-gray-300 text-sm mt-4">
                        🛡️ Run your first{" "}
                        <Link to="/it" className="text-teal-300 underline font-semibold">
                          ZelGuardian Monthly Checkup
                        </Link>{" "}
                        to raise your score and protect your accounts.
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* MY IMPACT — only rendered when donation data exists.
                  Proportional statement is computed from the public
                  transparency data, never fabricated per-dollar tracing. */}
              {impact && (
                <div className="mb-8 text-left">
                  <div
                    className="rounded-2xl p-6 mb-4 text-white shadow-lg"
                    style={{ background: "linear-gradient(90deg, #12bfbe, #9198e5)" }}
                  >
                    <h2 className="text-2xl font-extrabold mb-4">💙 My Impact</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
                      <div className="bg-white/15 rounded-xl p-4">
                        <p className="text-2xl font-extrabold">
                          {fmt(impact.thisYearCents)}
                        </p>
                        <p className="text-xs text-white/80">
                          {new Date().getFullYear()} Donations
                        </p>
                      </div>
                      <div className="bg-white/15 rounded-xl p-4">
                        <p className="text-2xl font-extrabold">
                          {fmt(impact.lifetimeCents)}
                        </p>
                        <p className="text-xs text-white/80">Lifetime Donations</p>
                      </div>
                      <div className="bg-white/15 rounded-xl p-4">
                        <p className="text-2xl font-extrabold">
                          {fmt(impact.totalDonatedCents)}
                        </p>
                        <p className="text-xs text-white/80">Total Donated</p>
                      </div>
                    </div>
                    <p className="text-sm text-white/90 mt-4 leading-relaxed">
                      You contributed {fmt(impact.thisYearCents)} in{" "}
                      {new Date().getFullYear()}. ZelTechnology allocated{" "}
                      {Math.round(
                        (breakdownPct("IT Equipment") || 0)
                      )}% of its expenditures to equipment,{" "}
                      {Math.round(breakdownPct("Community IT Services") || 0)}%
                      to community IT services,{" "}
                      {Math.round(breakdownPct("Education & Training") || 0)}%
                      to education, and{" "}
                      {Math.round(breakdownPct("Infrastructure / Hosting") || 0)}
                      % to infrastructure — helping anyone the digital age
                      overwhelms.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                    {impact.donations.map((d, i) => (
                      <div
                        key={i}
                        className="bg-gray-50 rounded-xl p-4 flex justify-between items-center"
                      >
                        <p className="text-gray-600">
                          {new Date(d.date).toLocaleDateString()}
                        </p>
                        <p className="font-bold text-blue-900">{fmt(d.amountCents)}</p>
                        <p className="text-teal-500 text-sm font-semibold">
                          ✓ {d.status === "received" ? "Received" : d.status}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* designated campaign card — aggregate only, no per-dollar claims */}
              {campaigns.filter((c) => c.active).map((c) => {
                const pct = Math.min(100, Math.round((c.raisedCents / c.goalCents) * 100));
                const dollars = (cents) => fmt(Math.round(cents / 100));
                return (
                  <div
                    key={c.id}
                    className="mb-8 text-left rounded-2xl p-6 text-white shadow-lg"
                    style={{ background: "linear-gradient(135deg, #1e3a8a, #36395a)" }}
                  >
                    <p className="font-extrabold text-lg mb-1">
                      {c.icon} {c.name}
                    </p>
                    <p className="text-gray-300 text-sm mb-4">{c.tagline}</p>
                    <div className="flex items-center gap-3 mb-2">
                      <div className="flex-1 h-3 bg-white/20 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full"
                          style={{
                            width: `${pct}%`,
                            background: "linear-gradient(90deg, #12bfbe, #9198e5)",
                          }}
                        />
                      </div>
                      <p className="font-bold text-sm whitespace-nowrap">{pct}%</p>
                    </div>
                    <p className="text-xs text-gray-300 mb-4">
                      {dollars(c.raisedCents)} raised of {dollars(c.goalCents)} goal
                    </p>
                    <Link
                      to="/transparency"
                      className="inline-block px-4 py-2 rounded-xl bg-teal-400 text-blue-900 text-sm font-bold hover:bg-teal-300 transition"
                    >
                      View campaign →
                    </Link>
                  </div>
                );
              })}

              {/* member services grid — links to member tools */}
              <div className="grid gap-4 sm:grid-cols-3 mb-8">
                <Link
                  to="/scam-check/analyze"
                  className="bg-teal-50 rounded-xl p-4 text-center border border-teal-200 hover:shadow-md transition"
                >
                  <p className="text-2xl mb-1">🛡️</p>
                  <p className="font-bold text-blue-900 text-sm">Scam Check</p>
                  <p className="text-gray-500 text-xs">Second opinion on suspicious messages</p>
                </Link>
                <Link
                  to="/devices"
                  className="bg-gray-50 rounded-xl p-4 text-center border border-gray-200 hover:shadow-md transition"
                >
                  <p className="text-2xl mb-1">🔐</p>
                  <p className="font-bold text-blue-900 text-sm">Device Locker</p>
                  <p className="text-gray-500 text-xs">Register your devices for support</p>
                </Link>
                <Link
                  to="/learn"
                  className="bg-gray-50 rounded-xl p-4 text-center border border-gray-200 hover:shadow-md transition"
                >
                  <p className="text-2xl mb-1">🎓</p>
                  <p className="font-bold text-blue-900 text-sm">ZelPath Learning</p>
                  <p className="text-gray-500 text-xs">Practical lessons + your Technology Health</p>
                </Link>
              </div>

              {/* MY REQUESTS — helpdesk tickets */}
              {tickets !== null && (
                <div className="mb-8 text-left">
                  <h2 className="text-2xl font-extrabold text-blue-900 mb-4">
                    🎫 My Requests
                  </h2>

                  {/* ticket list */}
                  {tickets.length > 0 && (
                    <div className="space-y-3 mb-5">
                      {tickets.map((t) => (
                        <button
                          key={t.id}
                          onClick={() => openTicket(t.id)}
                          className={`w-full text-left bg-gray-50 hover:bg-white rounded-xl p-4 border transition shadow-sm hover:shadow ${
                            ticketDetail?.ticket.id === t.id
                              ? "border-teal-400 ring-1 ring-teal-400"
                              : "border-gray-200"
                          }`}
                        >
                          <div className="flex justify-between items-start gap-2">
                            <div>
                              <p className="font-bold text-blue-900 text-sm">
                                #{t.id} · {t.subject}
                              </p>
                              <p className="text-gray-500 text-xs mt-0.5">
                                {categoryLabel[t.category] || t.category} ·{" "}
                                {new Date(t.updated_at).toLocaleDateString()}
                              </p>
                            </div>
                            <span
                              className={`text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap ${
                                statusBadge[t.status]?.cls || "bg-gray-100"
                              }`}
                            >
                              {statusBadge[t.status]?.label || t.status}
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                  {tickets.length === 0 && (
                    <p className="text-gray-500 text-sm mb-5">
                      No requests yet — create one below and track it here
                      like a package.
                    </p>
                  )}

                  {/* ticket detail view */}
                  {ticketDetail && (
                    <div className="bg-gray-50 rounded-xl p-5 mb-5 border border-teal-200">
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="font-extrabold text-blue-900">
                          #{ticketDetail.ticket.id} · {ticketDetail.ticket.subject}
                        </h3>
                        <button
                          onClick={() => setTicketDetail(null)}
                          className="text-gray-400 hover:text-gray-600 text-sm font-bold"
                          aria-label="Close ticket"
                        >
                          ✕
                        </button>
                      </div>
                      <div className="space-y-3 mb-4 max-h-72 overflow-y-auto">
                        {ticketDetail.messages.map((m, i) => (
                          <div
                            key={i}
                            className={`rounded-xl p-3 text-sm ${
                              m.author_name === member?.username
                                ? "bg-blue-50 ml-8"
                                : "bg-white mr-8 border border-gray-100"
                            }`}
                          >
                            <p className="text-xs text-gray-400 mb-1">
                              {m.author_name} ·{" "}
                              {new Date(m.created_at).toLocaleString()}
                            </p>
                            <p className="text-gray-700 whitespace-pre-wrap">
                              {m.body}
                            </p>
                          </div>
                        ))}
                      </div>
                      {ticketDetail.ticket.status !== "closed" && (
                        <form onSubmit={replyTicket} className="flex gap-2">
                          <input
                            type="text"
                            value={replyText}
                            onChange={(e) => setReplyText(e.target.value)}
                            placeholder="Add a reply…"
                            className="flex-1 px-3 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-900 text-sm"
                            maxLength={5000}
                            required
                          />
                          <button
                            type="submit"
                            className="px-4 py-2 rounded-lg bg-blue-900 text-white text-sm font-bold hover:bg-blue-800"
                          >
                            Reply
                          </button>
                        </form>
                      )}
                    </div>
                  )}

                  {/* new ticket form */}
                  <details className="bg-gray-50 rounded-xl p-4">
                    <summary className="cursor-pointer font-bold text-blue-900 text-sm">
                      ➕ Create a new request
                    </summary>
                    <form onSubmit={createTicket} className="flex flex-col gap-3 mt-4">
                      <select
                        value={newTicket.category}
                        onChange={(e) =>
                          setNewTicket({ ...newTicket, category: e.target.value })
                        }
                        className="px-3 py-2 rounded-lg border border-gray-300 text-sm"
                        aria-label="Request type"
                      >
                        <option value="general">General question</option>
                        <option value="remote">🖥️ Remote session</option>
                        <option value="hardware">🎁 Hardware assistance</option>
                        <option value="digital">📈 Digital Growth</option>
                        <option value="lamna">🦈 LAMNA follow-up</option>
                      </select>
                      <input
                        type="text"
                        placeholder="Subject (short summary)"
                        value={newTicket.subject}
                        onChange={(e) =>
                          setNewTicket({ ...newTicket, subject: e.target.value })
                        }
                        className="px-3 py-2 rounded-lg border border-gray-300 text-sm"
                        minLength={3}
                        maxLength={200}
                        required
                      />
                      <textarea
                        placeholder="Describe what's going on…"
                        value={newTicket.body}
                        onChange={(e) =>
                          setNewTicket({ ...newTicket, body: e.target.value })
                        }
                        className="px-3 py-2 rounded-lg border border-gray-300 text-sm"
                        rows={4}
                        maxLength={5000}
                        required
                      />
                      <button
                        type="submit"
                        className="self-start px-4 py-2 rounded-lg bg-blue-900 text-white text-sm font-bold hover:bg-blue-800"
                      >
                        Create Ticket
                      </button>
                    </form>
                  </details>
                </div>
              )}

              <button
                onClick={handleLogout}
                className="text-white bg-blue-900 hover:bg-blue-800 inline-flex items-center justify-center px-6 py-3 text-lg shadow-xl rounded-2xl"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Members;
